/// <reference types="@angular/localize" />

import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter, Router } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { AppRoutes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { environment, MAPBOX_TOKEN } from './environments/environment';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpLoaderFactory } from './app/helpers/languages/HttpLoaderFactory';
import { initializeLanguage } from './app/helpers/languages/initializeLanguage';
import { LanguageService } from './app/shared/services/language/language.service';
import { StorageService } from './app/shared/services/storage/storage.service';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { AuthInterceptor } from './app/interceptors/auth.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { IonicStorageModule, Storage } from '@ionic/storage-angular';
import { jwtOptionsFactory } from './app/helpers/jwt/JwtOptionsFactory';
import { provideMapboxGL } from 'ngx-mapbox-gl';
import * as Sentry from "@sentry/angular";

// Inicializar Sentry solo en producción
if (environment.production) {
  Sentry.init({
    dsn: "https://3c073843b9f7c6bd132cc07cace26dc0@o4504844807831552.ingest.us.sentry.io/4510028375588864",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.5, // Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.5, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideAnimationsAsync(),
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(AppRoutes),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false || 'none'
        }
      }
    }),

    importProvidersFrom(
      IonicStorageModule.forRoot(),
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: jwtOptionsFactory,
          deps: [Storage]
        }
      }),
      HttpClient,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    provideMapboxGL({
      accessToken: MAPBOX_TOKEN
    }),
    provideHttpClient(
      withInterceptorsFromDi()
    ), {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      deps: [TranslateService, LanguageService, StorageService],
      multi: true,
    },
  ],
}).catch(err => console.log(err));
